import * as signalR from '@microsoft/signalr';
import types from '../App/store/types';
import { videoActions } from '../components/pages/VideosPage/store';
import { createActions } from '../components/pages/CreatePage/store';
import { appOperations } from '../App/store';
import { exportsActions } from '../components/pages/ExportsPage/store';
import ColoredSnackbarConstants from '../components/misc/ColoredSnackbar/ColoredSnackbarConstants';
import videosPageConstants from '../components/pages/VideosPage/VideosPageConstants'
import { isAuthorizationTokenExpired } from '../utilities/authUtilities';
import { mediaPickerActions } from '../components/pages/CreatePage/MediaPickers/store';

let connection = null;

export const signalRMiddleware = store => next => action => {
    if (action.type !== types.SET_UP_SIGNALR)
        return next(action);

    if (connection)
        connection.stop();

    connection = new signalR.HubConnectionBuilder()
        .withUrl('/r', { accessTokenFactory: () => action.token })
        .withAutomaticReconnect()
        .build();

    connection.on('UpdateExportProgress', data => {
        if (!data.videoId || !data.exportId || !data.step)
            return;

        // Update the export progress for different places if the export is active.
        store.dispatch(videoActions.updateExportProgress(data.videoId, data.exportId, { step: data.step, value: data.value }));
        store.dispatch(exportsActions.updateExportProgress(data.exportId, { step: data.step, value: data.value }));
    });

    connection.on('SetExportSuccess', data => {
        if (!data.videoId || !data.exportId || !data.videoFileUrl)
            return;

        if (data.exportedBassMaxMagnitude || data.exportedWideMaxMagnitude)
            console.log('Export completed with max magnitudes:', data.exportedBassMaxMagnitude, data.exportedWideMaxMagnitude);

        // Set the successful export status.
        store.dispatch(appOperations.openSnack('Video finished exporting successfully!', ColoredSnackbarConstants.SNACKBAR_SUCCESS));
        store.dispatch(videoActions.setExportStatus(data.videoId, data.exportId, videosPageConstants.COMPLETED_EXPORT_STATUS));
        store.dispatch(exportsActions.setExportStatus(data.exportId, videosPageConstants.COMPLETED_EXPORT_STATUS, data.videoFileUrl));
        store.dispatch(exportsActions.setProperty('latestCompletedExportId', data.exportId));

        // Clear the the export progress for different places.
        store.dispatch(videoActions.updateExportProgress(data.videoId, data.exportId, null));
        store.dispatch(exportsActions.updateExportProgress(data.exportId, null));
    });

    connection.on('SetExportFail', data => {
        if (!data.videoId || !data.exportId)
            return;

        // Set the failed export status.
        store.dispatch(appOperations.openSnack('Video export failed.', ColoredSnackbarConstants.SNACKBAR_ERROR));
        store.dispatch(videoActions.setExportStatus(data.videoId, data.exportId, videosPageConstants.FAILED_EXPORT_STATUS));
        store.dispatch(exportsActions.setExportStatus(data.exportId, videosPageConstants.FAILED_EXPORT_STATUS, undefined, data.rendererFailureCode));
        store.dispatch(exportsActions.setProperty('latestCompletedExportId', data.exportId));

        // Clear the the export progress for different places.
        store.dispatch(videoActions.updateExportProgress(data.videoId, data.exportId, null));
        store.dispatch(exportsActions.updateExportProgress(data.exportId, null));
    });

    connection.on('UpdateVideoList', data => {
        store.dispatch(videoActions.updateVideos(data));
    });

    connection.on('LyricExtractionProgress', data => {
        const videoId = data?.videoId || data?.VideoId;
        const percentage = data?.percentage ?? data?.Percentage;
        if (!videoId)
            return;

        // Only apply progress if the event is for the currently open video project.
        const currentVideoId = store.getState().createPreview?.present?.videoSettings?.id;
        if (currentVideoId && currentVideoId !== videoId)
            return;

        store.dispatch(createActions.setProperty('lyricExtractionProgress', percentage));
    });

    connection.on('LyricExtractionCompleted', data => {
        const videoId = data?.videoId || data?.VideoId;
        const segments = data?.segments || data?.Segments;
        if (!videoId || !segments)
            return;

        // Only apply if the event is for the currently open video project.
        const currentVideoId = store.getState().createPreview?.present?.videoSettings?.id;
        if (currentVideoId && currentVideoId !== videoId)
            return;

        store.dispatch(createActions.setProperty('lyricExtractionProcessing', false));
        store.dispatch(createActions.setProperty('lyricExtractionProgress', null));
        store.dispatch(createActions.setProperty('lyricExtractionJobId', null));
        store.dispatch(createActions.applyDetectedLyricsSegments(segments));
        store.dispatch(appOperations.openSnack('Lyrics detected and applied.', ColoredSnackbarConstants.SNACKBAR_SUCCESS));
    });

    connection.on('LyricExtractionFailed', data => {
        const videoId = data?.videoId || data?.VideoId;
        if (!videoId)
            return;

        // Only apply if the event is for the currently open video project.
        const currentVideoId = store.getState().createPreview?.present?.videoSettings?.id;
        if (currentVideoId && currentVideoId !== videoId)
            return;

        store.dispatch(createActions.setProperty('lyricExtractionProcessing', false));
        store.dispatch(createActions.setProperty('lyricExtractionProgress', null));
        store.dispatch(createActions.setProperty('lyricExtractionJobId', null));
        store.dispatch(appOperations.openSnack('Auto Detect failed.', ColoredSnackbarConstants.SNACKBAR_ERROR));
    });

    connection.on('RemoveExpiredVideo', data => {
        // Remove the video project from videos list
        store.dispatch(videoActions.removeVideoItem(data.videoId));

        const currentPath = window.location.pathname;
        const expiredVideoOpen = (currentPath.includes('/create') || currentPath.includes('/exports')) && currentPath.includes(data.videoId);

        // Show a message on the pages where user can see expired video project 
        if (expiredVideoOpen || currentPath.includes('/videos')) {
            store.dispatch(appOperations.openSnack('Expired video projects have been removed', ColoredSnackbarConstants.SNACKBAR_WARNING));
        }

        // Redirect to the videos page if a user is on the page related to expired video project 
        if (expiredVideoOpen) {
            window.location.replace('/videos');
        }
    });

    connection.on('UpdatePresetsList', () => {
        store.dispatch(createActions.getPresets());
    });

    connection.on('RemoveExpiredExport', data => {
        // Remove the video export from exports list
        store.dispatch(exportsActions.removeExportFromList(data.exportId));
        store.dispatch(videoActions.removeVideoExport(data.videoId, data.exportId));
    });

    connection.on('UpdateExportsPosition', () => {
        if (!isAuthorizationTokenExpired()) {
            store.dispatch(createActions.getQueuedExportsCount());
            store.dispatch(exportsActions.loadQueuePositions());
        }
    });

    connection.on('AddUserAsset', (data) => store.dispatch(mediaPickerActions.loadNewAsset(data.assetId, data.mediaResourceType, data.mediaUsageType)));
    connection.on('RemoveUserAsset', (data) => store.dispatch(mediaPickerActions.removeUserAsset(data.assetId, data.mediaUsageType)));
    connection.start();
}